/* eslint-disable */
/**
 * Composición final del modelo de presentación consumido por React.
 *
 * Estos métodos operan sobre la instancia de PanelLogic y conservan el
 * contrato del prototipo importado. No mantienen estado global propio.
 */
const logic = {
  renderVals() {
    return this.roundAll(this._renderVals());
  },
  _renderVals() {
    if (this.data.empty) {
      const st = this.state;
      return {
        ...this.drawerVals(st),
        ...this.calVals(st),
        noData: true,
        stopProp: e => e.stopPropagation(),
        vsHdr: false,
        vsNoHdr: true,
        viewPanel: false,
        viewSimple: false,
        viewVs: false,
        viewFs: false,
        viewCmp: false,
        viewCz: false,
        viewDep: false,
        showRng: false,
        czRuleOn: false
      };
    }
    const D = this.data,
      st = this.state,
      N = D.days.length;
    const tol = this.props.sfrTolerance ?? 5,
      gap = this.props.cellGap ?? 2;
    const stripes = "repeating-linear-gradient(135deg,#2a3446 0 0.277778vh,#0f1623 0.277778vh 0.555556vh)";
    const isAl = st.hmMetric === 'alarms';
    const maxAl = Math.max(...D.rows.flatMap(r => r.cells.map(c => isAl ? c.al : c.apv)));
    const hm = kind => D.rows.map((r, ci) => ({
      name: r.name,
      nameColor: ci === st.sel ? '#e4e9f0' : '#8793a6',
      nameWeight: ci === st.sel ? 600 : 400,
      onName: () => this.setState({
        sel: ci
      }),
      cells: r.cells.map((c, di) => ({
        bg: kind === 'al' ? c.ay ? stripes : this.alC(isAl ? c.al : c.apv, maxAl) : this.sfrC(c.sfr, tol, stripes),
        ol: ci === st.sel && di === st.day ? "0.185185vh solid #e4e9f0" : 'none',
        enter: () => {
          const h = this.state.hover;
          if (!h || h.di !== di) this.setState({
            hover: {
              ci,
              di
            }
          });
        },
        click: () => this.setState({
          sel: ci,
          day: di
        })
      }))
    }));
    let hoverText = '';
    if (st.hover) {
      const r = D.rows[st.hover.ci],
        c = r.cells[st.hover.di];
      hoverText = c.ay ? `${r.name} · ${D.days[st.hover.di]} · ayuno` : `${r.name} · ${D.days[st.hover.di]} · ${c.al} alarmas (${c.apv.toFixed(2)}/visita) · SFR ${c.sfr} %`;
    }
    const weeks = [];
    for (let i = 0; i < N; i += 7) weeks.push({
      lab: N - i < 4 ? '' : D.days[i],
      span: Math.min(7, N - i)
    });
    const sum = D.rows.map((r, ci) => {
      return {
        ci,
        name: r.name,
        sfr: this.v1MetricAggregate('sfr', r.cells) ?? 0,
        alarms: this.v1MetricAggregate('alarms', r.cells) ?? 0,
        apv: this.v1MetricAggregate('apv', r.cells) ?? 0,
        ppa: this.v1MetricAggregate('ppa', r.cells) ?? 0,
        ay: r.cells.filter(c => c.ay).length
      };
    });
    const mx = k => Math.max(...sum.map(s => s[k]), 1);
    const colDefs = [['sfr', 'SFR prom.'], ['alarms', 'Alarmas'], ['apv', 'Alarmas / visita'], ['ppa', 'Pellets perdidos'], ['ay', 'Ayunos']];
    const cols = colDefs.map(([key, label]) => ({
      label,
      arrow: st.sortKey === key ? st.sortDir < 0 ? '↓' : '↑' : '',
      color: st.sortKey === key ? '#e4e9f0' : '#8793a6',
      onClick: () => this.setState(s => ({
        sortKey: key,
        sortDir: s.sortKey === key ? -s.sortDir : -1
      }))
    }));
    const sorted = [...sum].sort((a, b) => (a[st.sortKey] - b[st.sortKey]) * st.sortDir);
    const pct = (v, m) => (v / m * 100).toFixed(1) + '%';
    const tableRows = sorted.map(s => {
      const d = s.sfr - 100;
      return {
        name: s.name,
        bg: s.ci === st.sel ? 'rgba(46,179,238,0.12)' : 'transparent',
        onClick: () => this.setState(x => ({
          sel: s.ci,
          cmp: (x.cmp || [x.sel]).includes(s.ci) ? x.cmp : [...(x.cmp || [x.sel]), s.ci].slice(-4)
        })),
        sfrTxt: s.sfr.toFixed(1) + ' %',
        sfrW: pct(Math.min(s.sfr, 150), 150),
        sfrBar: Math.abs(d) <= tol ? '#8793a6' : d < 0 ? 'oklch(0.7 0.14 250)' : 'oklch(0.72 0.15 55)',
        alTxt: String(s.alarms),
        alW: pct(s.alarms, mx('alarms')),
        apvTxt: s.apv.toFixed(2),
        apvW: pct(s.apv, mx('apv')),
        ppaTxt: String(s.ppa),
        ppaW: pct(s.ppa, mx('ppa')),
        ayTxt: s.ay + (s.ay === 1 ? ' día' : ' días'),
        ayW: pct(s.ay, mx('ay'))
      };
    });
    const tc = {
        Baja: '#1f8f8c',
        Media: '#a3621f',
        Alta: '#a8264f'
      },
      tcT = {
        Baja: '#4cc3bf',
        Media: '#d98b3f',
        Alta: '#e0577f'
      };
    const tr = {};
    ['Baja', 'Media', 'Alta'].forEach(k => {
      // Range over every individual rate reported for the tier, as v1 lists them.
      const v = D.tiers[k].flatMap(e => e.rates || [e.rv]).filter(Number.isFinite);
      tr[k] = v.length ? +Math.min(...v).toFixed(2) + '–' + +Math.max(...v).toFixed(2) : '–';
    });
    const tH = st.tierHov,
      tAll = ['Baja', 'Media', 'Alta'].reduce((s, k) => s + D.tiers[k].reduce((a, e) => a + e.n, 0), 0);
    let tierTip = {
      on: false,
      kpis: [],
      cages: [],
      last: []
    };
    if (tH) {
      const L = D.tiers[tH],
        n = L.reduce((a, e) => a + e.n, 0),
        byC = {},
        byD = {};
      L.forEach(e => {
        byC[e.cages] = (byC[e.cages] || 0) + e.n;
        byD[e.date] = (byD[e.date] || 0) + e.n;
      });
      const cs = Object.entries(byC).sort((a, b) => b[1] - a[1]).slice(0, 3),
        cMx = cs.length ? cs[0][1] : 1,
        nd = Object.keys(byD).length,
        j = ['Baja', 'Media', 'Alta'].indexOf(tH);
      // Same list as the v1 doughnut tooltip: one row per (day, rate) with the
      // cages that reported it. Alarm counts are per tier and day, not per rate,
      // so no count is attributed to individual rates.
      const byDR = new Map();
      L.forEach(e => (e.rates || []).forEach(rv => {
        const key = e.date + '|' + rv;
        if (!byDR.has(key)) byDR.set(key, {
          d: e.date,
          rv,
          cages: new Set()
        });
        byDR.get(key).cages.add(e.cages);
      }));
      const dayMs = d => {
        const [dd, mm, yy] = String(d).split('/');
        return Date.UTC(+yy, +mm - 1, +dd);
      };
      const rates = [...byDR.values()].sort((a, b) => dayMs(a.d) - dayMs(b.d) || a.rv - b.rv).map(g => ({
        v: +g.rv.toFixed(2),
        d: g.d,
        cages: [...g.cages].sort((a, b) => String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: 'base'
        })).join(', ')
      }));
      tierTip = {
        on: true,
        rates,
        nR: rates.length + (rates.length === 1 ? ' tasa' : ' tasas'),
        keep: () => this.setState({
          tierHov: tH
        }),
        label: tH,
        c: tcT[tH],
        range: tr[tH],
        left: j === 0 ? '0' : j === 1 ? '50%' : '100%',
        tx: j === 0 ? 'none' : j === 1 ? 'translateX(-50%)' : 'translateX(-100%)',
        kpis: [{
          l: 'Alarmas',
          v: String(n)
        }, {
          l: '% del total',
          v: (tAll ? n / tAll * 100 : 0).toFixed(0) + ' %'
        }, {
          l: 'Por día',
          v: (nd ? n / D.days.length : 0).toFixed(1)
        }],
        cages: cs.map(([k, v]) => ({
          n: k,
          v: String(v),
          w: (v / cMx * 100).toFixed(0) + '%'
        })),
        last: L.slice(-3).reverse().map(e => ({
          d: e.date,
          n: e.cages,
          r: e.rate
        }))
      };
    }
    const tierSegs = ['Baja', 'Media', 'Alta'].map(t => ({
      enter: () => this.setState({
        tierHov: t
      }),
      hbd: tH === t ? tc[t] : '#1c2636',
      hbg: tH === t ? 'rgba(46,179,238,0.06)' : 'transparent',
      label: t,
      n: D.tiers[t].reduce((a, e) => a + e.n, 0),
      color: tc[t],
      range: tr[t],
      op: st.tier === t ? 1 : 0.45,
      border: st.tier === t ? '#3a475c' : '#1c2636',
      onClick: () => this.setState({
        tier: t
      })
    }));
    const pMax = Math.max(1, Math.ceil(Math.max(...D.rows.map(r => r.cells[N - 1].cum)) / 50) * 50);
    const ppaLines = D.rows.map((r, ci) => ({
      ci,
      d: r.cells.map((c, di) => (di ? 'L' : 'M') + (di / Math.max(1, N - 1) * 600).toFixed(1) + ' ' + (100 - c.cum / pMax * 100).toFixed(2)).join(' '),
      stroke: ci === st.sel ? 'oklch(0.8 0.13 85)' : '#3a475c',
      w: ci === st.sel ? 2.5 : 1.25,
      label: r.name + ' · ' + r.cells[N - 1].cum,
      lc: ci === st.sel ? 'oklch(0.8 0.13 85)' : '#8793a6',
      actualTop: Math.max(0, Math.min(100, 100 - r.cells[N - 1].cum / pMax * 100)),
      onClick: () => this.setState({
        sel: ci
      })
    }));
    // Keep all labels within the card and separate cages that finish at the
    // same value. The backward pass prevents the final labels from overflowing.
    const labelMin = 2.5,
      labelMax = 97.5,
      labelGap = Math.min(4.2, ppaLines.length > 1 ? (labelMax - labelMin) / (ppaLines.length - 1) : 4.2),
      byTop = [...ppaLines].sort((a, b) => a.actualTop - b.actualTop);
    let prev = labelMin - labelGap;
    byTop.forEach(l => {
      const t = Math.max(labelMin, l.actualTop, prev + labelGap);
      l.labelTop = t;
      prev = t;
    });
    for (let i = byTop.length - 1; i >= 0; i--) {
      const ceiling = i === byTop.length - 1 ? labelMax : byTop[i + 1].labelTop - labelGap;
      byTop[i].labelTop = Math.max(labelMin, Math.min(byTop[i].labelTop, ceiling));
      byTop[i].top = byTop[i].labelTop.toFixed(1) + '%';
      byTop[i].actualTop = byTop[i].actualTop.toFixed(1) + '%';
    }
    ppaLines.sort((a, b) => (a.ci === st.sel) - (b.ci === st.sel));
    const ppaHoverDay = Number.isInteger(st.ppaHoverDay) && st.ppaHoverDay >= 0 && st.ppaHoverDay < N ? st.ppaHoverDay : null;
    const ppaDayFromPointer = e => {
      const rect = e.currentTarget.getBoundingClientRect(),
        ratio = rect.width ? (e.clientX - rect.left) / rect.width : 0;
      return Math.max(0, Math.min(N - 1, Math.round(Math.max(0, Math.min(1, ratio)) * Math.max(1, N - 1))));
    };
    const ppaSelectStep = direction => this.setState(s => ({
      sel: (s.sel + direction + D.rows.length) % D.rows.length
    }));
    const ppaMove = e => {
      const di = ppaDayFromPointer(e);
      if (di !== this.state.ppaHoverDay) this.setState({
        ppaHoverDay: di
      });
    };
    const ppaWheel = e => {
      if (!e.deltaY || !D.rows.length) return;
      const now = Date.now();
      if (now - (this._ppaWheelAt || 0) < 120) return;
      this._ppaWheelAt = now;
      ppaSelectStep(e.deltaY > 0 ? 1 : -1);
    };
    const ppaKeyDown = e => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowUp' && e.key !== 'ArrowRight' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      ppaSelectStep(e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1);
    };
    const ppaTipRows = ppaHoverDay == null ? [] : D.rows.map((r, ci) => ({
      name: r.name,
      val: String(r.cells[ppaHoverDay].cum),
      value: r.cells[ppaHoverDay].cum,
      c: ci === st.sel ? 'oklch(0.8 0.13 85)' : '#8793a6',
      fw: ci === st.sel ? 700 : 400,
      selected: ci === st.sel
    })).sort((a, b) => a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: 'base'
    }));
    const ppaTickIndices = [...new Set([0, .25, .5, .75, 1].map(f => Math.round(f * Math.max(0, N - 1))))];
    const shortDay = day => String(day || '').split('/').slice(0, 2).join('/');
    const cell = D.rows[st.sel].cells[st.day];
    const det = this.detail(st.sel, st.day);
    return {
      gap,
      hoverText,
      weeks,
      clearHover: () => this.setState({
        hover: null
      }),
      metricOpts: [['alarms', 'Total'], ['apv', 'Por visita']].map(([k, label]) => ({
        label,
        bg: st.hmMetric === k ? 'rgba(46,179,238,0.16)' : 'transparent',
        color: st.hmMetric === k ? '#e4e9f0' : '#8793a6',
        onClick: () => this.setState({
          hmMetric: k
        })
      })),
      alLegend: [0.2, 0.4, 0.6, 0.8, 1].map(t => this.alC(t * maxAl, maxAl)),
      alMaxTxt: isAl ? String(maxAl) : maxAl.toFixed(1),
      sfrLegend: [72, 82, 92, 100, 108, 118, 128].map(v => this.sfrC(v, tol, stripes)),
      ...this.sfrCard(st, tol),
      ...this.evoZoom(st, this.evoSimple(st.sMetric === 'sfr' ? {
        ...st,
        sMetric: 'alarms'
      } : st, tol)),
      ...this.cmpVals(st),
      ...(() => {
        const ex = st.exp,
          mk = (k, name) => ({
            icon: ex === k ? '⤡' : '⤢',
            label: ex === k ? 'Contraer' : 'Expandir',
            title: ex === k ? 'Volver al panel (Esc)' : 'Usar toda la pantalla para ' + name,
            onClick: () => this.setState({
              exp: ex === k ? null : k
            })
          });
        return {
          gridCols: ex ? 'minmax(0,1fr)' : "25vw minmax(0,1fr)",
          gridRows: ex || st.view === 'dep' ? 'minmax(0,1fr)' : "minmax(0,1fr) 36.111111vh",
          gridGap: 12,
          leftDisp: ex ? 'none' : 'flex',
          overviewCol: '1',
          overviewRow: ex || st.view === 'dep' || det.alarms.length === 0 ? '1' : '1 / 3',
          detailCol: ex ? '1' : '2',
          detailRow: '1',
          bottomCol: ex ? '1' : det.alarms.length === 0 ? '1 / 3' : '2',
          bottomRow: ex ? '1' : '2',
          detDisp: ex === 'evo' ? 'none' : 'flex',
          evoDisp: ex === 'det' || st.view === 'dep' ? 'none' : 'flex',
          evoFlex: ex === 'evo' ? '1' : 'none',
          evoH: ex === 'evo' ? 'auto' : "36.111111vh",
          evoDir: ex === 'evo' ? 'column' : 'row',
          sfrFlex: 'none',
          sfrW: ex === 'evo' ? 'auto' : det.alarms.length === 0 ? "46vw" : "29vw",
          sfrH: ex === 'evo' ? "31.481481vh" : 'auto',
          dH: ex === 'det' ? {
            a: 310,
            o: 180,
            c: 130,
            d: 160,
            l: 82
          } : {
            a: 145,
            o: 80,
            c: 60,
            d: 75,
            l: 92
          },
          expCagesOn: ex === 'evo',
          ...(() => {
            const pal = ['oklch(0.78 0.12 250)', 'oklch(0.78 0.14 160)', 'oklch(0.72 0.16 320)', 'oklch(0.84 0.14 90)', 'oklch(0.72 0.17 25)', 'oklch(0.78 0.11 200)', 'oklch(0.74 0.15 55)', 'oklch(0.7 0.12 290)'];
            const cmp = (st.cmp8 || []).filter(c => c !== st.sel),
              order = [st.sel, ...cmp];
            const tot = D.rows.map((r, ci) => ({
              ci,
              al: this.v1MetricAggregate('alarms', r.cells) ?? 0
            })).sort((a, b) => b.al - a.al);
            return {
              expCages: D.rows.map((r, ci) => {
                const on = order.includes(ci),
                  main = ci === st.sel,
                  c = pal[order.indexOf(ci) % pal.length];
                return {
                  name: r.name,
                  dot: on ? c : '#3a475c',
                  bd: on ? c : 'rgba(46,179,238,0.14)',
                  bg: on ? 'rgba(46,179,238,0.10)' : 'transparent',
                  txt: on ? '#e4e9f0' : '#8793a6',
                  fw: main ? 700 : 400,
                  title: main ? 'Jaula principal' : on ? 'Quitar de la comparación' : 'Agregar a la comparación',
                  onClick: () => {
                    if (main) return;
                    this.setState(x => {
                      const c2 = x.cmp8 || [];
                      return {
                        cmp8: c2.includes(ci) ? c2.filter(y => y !== ci) : [...c2, ci]
                      };
                    });
                  }
                };
              }),
              expTop3: () => this.setState({
                cmp8: tot.map(t => t.ci).filter(c => c !== st.sel).slice(0, 3)
              }),
              expAll: () => this.setState({
                cmp8: D.rows.map((_, ci) => ci).filter(c => c !== st.sel)
              }),
              expNone: () => this.setState({
                cmp8: []
              })
            };
          })(),
          expDet: mk('det', 'el detalle diario'),
          expEvo: mk('evo', 'la evolución')
        };
      })(),
      ...(() => {
        if (!st.sMix) return {};
        const M = (st.sMixM || ['alarms', 'ppa', 'visitas']).filter(k => k !== 'sfr'),
          GL = ['●', '▲', '■', '◆', '✕', '▼'];
        const all = [['alarms', 'Alarmas'], ['apv', 'Al./visita'], ['apt', 'Al./tonelada'], ['ppa', 'Pellets perdidos'], ['visitas', 'Visitas'], ['kg', 'Alimento entregado']];
        return {
          sMainM: all.map(([k, label]) => {
            const i = M.indexOf(k),
              on = i >= 0;
            return {
              label: label + (on ? ' ✓' : ''),
              caret: '',
              bg: on ? 'rgba(46,179,238,0.16)' : 'transparent',
              color: on ? '#e4e9f0' : '#8793a6',
              fw: on ? 600 : 400,
              onClick: () => this.setState({
                sMixM: on ? M.length > 1 ? M.filter(x => x !== k) : M : [...M, k].slice(0, 6)
              })
            };
          }),
          sHasSub: false,
          sSubM: [],
          sSubTitle: ''
        };
      })(),
      mixBtn: {
        onClick: () => this.setState(s => ({
          sMix: !s.sMix,
          sMetric: s.sMetric === 'ppacum' ? 'ppa' : s.sMetric
        })),
        bd: st.sMix ? 'oklch(0.78 0.12 250)' : '#1c2636',
        bg: st.sMix ? 'rgba(46,179,238,0.12)' : 'transparent',
        color: st.sMix ? '#e4e9f0' : '#8793a6',
        track: st.sMix ? 'oklch(0.6 0.12 250)' : '#2a3446',
        knob: st.sMix ? "1.296296vh" : "0.185185vh"
      },
      showAxes: !st.sMix,
      ...(() => {
        if (st.sMix) return {};
        const cur = st.sMetric || 'alarms';
        const G = [['al', 'Alarmas', [['alarms', 'Alarmas'], ['apv', 'Por visita'], ['apt', 'Por tonelada']]], ['pp', 'Pellets perdidos', [['ppa', 'Por día'], ['ppacum', 'Acumulados del período']]], ['vi', 'Visitas', [['visitas']]], ['kg', 'Alimento entregado', [['kg']]]];
        const g = G.find(x => x[2].some(s => s[0] === cur)) || G[0];
        return {
          sMainM: G.map(x => {
            const on = x === g;
            return {
              label: x[1],
              caret: x[2].length > 1 ? '▾' : '',
              bg: on ? 'rgba(46,179,238,0.16)' : 'transparent',
              color: on ? '#e4e9f0' : '#8793a6',
              fw: on ? 600 : 400,
              onClick: () => {
                if (!on) this.setState({
                  sMetric: x[2][0][0]
                });
              }
            };
          }),
          sHasSub: g[2].length > 1,
          sSubTitle: g[1] + ':',
          sSubM: g[2].length > 1 ? g[2].map(([k, label]) => ({
            label,
            bg: k === cur ? '#2a3a55' : 'transparent',
            color: k === cur ? '#e4e9f0' : '#8793a6',
            fw: k === cur ? 600 : 400,
            onClick: () => this.setState({
              sMetric: k
            })
          })) : []
        };
      })(),
      ...(() => {
        const key = ['name', 'al', 'ay', 'ppa', 'sc'].includes(st.rkKey) ? st.rkKey : 'name',
          dir = st.rkDir || (key === 'name' ? 1 : -1);
        const pal = ['oklch(0.78 0.12 250)', 'oklch(0.78 0.14 160)', 'oklch(0.72 0.16 320)', 'oklch(0.84 0.14 90)', 'oklch(0.72 0.17 25)', 'oklch(0.78 0.11 200)', 'oklch(0.74 0.15 55)', 'oklch(0.7 0.12 290)'];
        const cmp = st.cmp8 || [],
          order = [st.sel, ...cmp.filter(c => c !== st.sel)];
        const SD = this.scoreData(),
          S = D.rows.map((r, ci) => {
            const v = SD.M[ci].filter(Boolean);
            return {
              ci,
              name: r.name,
              al: this.v1MetricAggregate('alarms', r.cells) ?? 0,
              // v1 ranking sums fasts_count (half days count 0.5).
              ay: r.cells.reduce((a, c) => a + (Number.isFinite(c.fasts) ? c.fasts : c.ay ? 1 : 0), 0),
              ppa: this.v1MetricAggregate('ppa', r.cells) ?? 0,
              sc: v.length ? v.reduce((a, c) => a + c.score, 0) / v.length : null
            };
          });
        const mx = k => Math.max(...S.map(s => s[k]), 1);
        const sorted = [...S].sort((a, b) => key === 'name' ? String(a.name).localeCompare(String(b.name), undefined, {
          numeric: true,
          sensitivity: 'base'
        }) * dir : ((a[key] ?? -Infinity) - (b[key] ?? -Infinity)) * dir);
        const labs = {
          al: 'Alarmas',
          ay: 'Ayunos',
          ppa: 'PPA acum.',
          sc: 'Score'
        };
        return {
          stopProp: e => e.stopPropagation(),
          rkScroll: () => this.state.rkHover != null && this.setState({
            rkHover: null,
            rkHd: null
          }),
          rkRanges: [[7, '7 d'], [14, '14 d'], [30, '30 d'], [58, 'Todo']].map(([k, label]) => {
            const on = (st.rkRange || 14) === k;
            return {
              label,
              bg: on ? 'rgba(46,179,238,0.16)' : 'transparent',
              color: on ? '#e4e9f0' : '#8793a6',
              onClick: e => {
                e.stopPropagation();
                this.setState({
                  rkRange: k
                });
              }
            };
          }),
          rkAll: (() => {
            const n = D.rows.length - 1,
              c = (st.cmp8 || []).filter(x => x !== st.sel).length,
              all = c >= n,
              some = c > 0 && !all;
            return {
              tick: all ? '✓' : some ? '–' : '',
              bg: all || some ? 'oklch(0.75 0.11 190)' : 'transparent',
              bd: all || some ? 'oklch(0.75 0.11 190)' : '#5a667a',
              title: all ? 'Quitar todas de la comparación' : 'Seleccionar todas las jaulas',
              onClick: e => {
                e.stopPropagation();
                this.setState({
                  cmp8: all ? [] : D.rows.map((_, ci) => ci).filter(x => x !== st.sel)
                });
              }
            };
          })(),
          rkName: {
            label: 'Jaula',
            arrow: key === 'name' ? dir < 0 ? ' ↓' : ' ↑' : '',
            color: key === 'name' ? '#e4e9f0' : '#8793a6',
            fw: key === 'name' ? 700 : 400,
            onClick: () => this.setState(x => ({
              rkKey: 'name',
              rkDir: x.rkKey === 'name' ? -(x.rkDir || 1) : 1
            }))
          },
          rkCols: ['al', 'ay', 'ppa', 'sc'].map(k => ({
            label: labs[k],
            arrow: k === key ? dir < 0 ? ' ↓' : ' ↑' : '',
            color: k === key ? '#e4e9f0' : '#8793a6',
            fw: k === key ? 700 : 400,
            onClick: () => this.setState(x => ({
              rkKey: k,
              rkDir: x.rkKey === k ? -(x.rkDir || -1) : -1
            }))
          })),
          rkRows: sorted.map((s, i) => {
            const ci = s.ci,
              on = ci === st.sel || cmp.includes(ci),
              col = pal[order.indexOf(ci) % pal.length],
              hov = st.rkHover === ci;
            let tip = {
              tipD: 'none',
              tDays: [],
              tNote: '',
              tScore: '—',
              tAl: '0',
              tAy: '0 días',
              tPpa: '0',
              tipTop: '50%',
              tipTx: 'translateY(-50%)',
              tRange: '',
              tHov: '',
              tAlMax: '',
              tCumMax: '',
              tX0: '',
              tX1: '',
              tVB: '0 0 1 100',
              tBars: 'M0 0',
              tAyBars: 'M0 0',
              tCum: 'M0 0',
              tHovBar: 'M0 0',
              chMove: null,
              chLeave: null
            };
            if (hov) {
              const N = D.days.length,
                len = st.rkRange || 14,
                d1 = len >= N ? N - 1 : Math.min(N - 1, Math.max(len - 1, st.day)),
                d0 = Math.max(0, d1 - Math.min(len, N) + 1);
              const C = D.rows[ci].cells,
                rng = [];
              for (let d = d0; d <= d1; d++) rng.push(d);
              const n = rng.length;
              const alMax = Math.max(1, ...rng.map(d => C[d].al));
              let cum = 0;
              const cumA = rng.map(d => cum += C[d].ppa);
              const cMax = Math.max(1, cum);
              const scoreA = rng.map(d => SD.M[ci][d]?.score);
              const scores = scoreA.filter(Number.isFinite);
              const scoreAvg = scores.length ? scores.reduce((total, score) => total + score, 0) / scores.length : null;
              const hd = st.rkHd != null && st.rkHd < n ? st.rkHd : null;
              const f2 = v => v.toFixed(2);
              const days = rng.map(d => ({
                c: C[d],
                d
              }));
              const rangeCells = days.map(({ c }) => c);
              const rangeAlarms = this.v1MetricAggregate('alarms', rangeCells) ?? 0;
              const rangeFasts = rangeCells.reduce((total, cell) => total + (Number.isFinite(cell.fasts) ? cell.fasts : cell.ay ? 1 : 0), 0);
              const rangePpa = this.v1MetricAggregate('ppa', rangeCells) ?? 0;
              tip = {
                tipD: 'flex',
                tNote: n + ' días · ' + days.filter(o => o.c.al > 0).length + ' con alarmas · ' + days.filter(o => o.c.ay).length + ' ayunos',
                tScore: scoreAvg == null ? '—' : scoreAvg.toFixed(1),
                tAl: String(rangeAlarms),
                tAy: rangeFasts + (rangeFasts === 1 ? ' día' : ' días'),
                tPpa: String(rangePpa),
                tRange: D.days[d0] + ' – ' + D.days[d1],
                tX0: D.days[d0],
                tX1: D.days[d1],
                tAlMax: String(alMax),
                tCumMax: String(cMax),
                tVB: '0 0 ' + n + ' 100',
                tBars: rng.map((d, i) => C[d].al ? 'M' + f2(i + 0.15) + ' 100 V' + f2(100 - C[d].al / alMax * 92) + ' H' + f2(i + 0.85) + ' V100 Z' : '').join(' ') || 'M0 0',
                tAyBars: rng.map((d, i) => C[d].ay ? 'M' + f2(i + 0.15) + ' 100 V0 H' + f2(i + 0.85) + ' V100 Z' : '').join(' ') || 'M0 0',
                tCum: cumA.map((v, i) => (i ? 'L' : 'M') + f2(i + 0.5) + ' ' + f2(100 - v / cMax * 92)).join(' '),
                tHovBar: hd == null ? 'M0 0' : 'M' + hd + ' 0 H' + (hd + 1) + ' V100 H' + hd + ' Z',
                tHov: hd == null ? 'Pasa el cursor por el gráfico' : D.days[rng[hd]] + ' · ' + (C[rng[hd]].ay ? 'ayuno' : C[rng[hd]].al + ' alarmas · PPA ' + C[rng[hd]].ppa) + ' · acum. ' + cumA[hd] + ' · score ' + (Number.isFinite(scoreA[hd]) ? scoreA[hd].toFixed(1) : '—'),
                chMove: e => {
                  const r = e.currentTarget.getBoundingClientRect(),
                    k = Math.max(0, Math.min(n - 1, Math.floor((e.clientX - r.left) / r.width * n)));
                  if (k !== this.state.rkHd) this.setState({
                    rkHd: k
                  });
                },
                chLeave: () => this.setState({
                  rkHd: null
                }),
                // Keep the tooltip vertically fixed beside the ranking card.
                tipTop: '50%',
                tipTx: 'translateY(-50%)',
                tDays: days.slice().reverse().map(({
                  c,
                  d
                }) => ({
                  key: String(d),
                  date: D.days[d],
                  fw: d === st.day ? 700 : 400,
                  bg: hd != null && rng[hd] === d ? 'rgba(46,179,238,0.16)' : d === st.day ? 'rgba(46,179,238,0.08)' : 'transparent',
                  al: c.al ? String(c.al) : '--',
                  alC: c.al ? 'oklch(0.72 0.17 25)' : '#3a475c',
                  ay: c.ay ? '1' : '--',
                  ayC: c.ay ? 'oklch(0.72 0.12 300)' : '#3a475c',
                  ppa: c.ppa ? String(c.ppa) : '--',
                  ppC: c.ppa ? 'oklch(0.8 0.13 85)' : '#3a475c',
                  score: Number.isFinite(SD.M[ci][d]?.score) ? SD.M[ci][d].score.toFixed(1) : '--',
                  scoreC: Number.isFinite(SD.M[ci][d]?.score) ? this.scoreC(SD.M[ci][d].score, SD.flat) : '#3a475c'
                }))
              };
            }
            return {
              ...tip,
              name: s.name,
              al: String(s.al),
              ay: s.ay + (s.ay === 1 ? ' día' : ' días'),
              ppa: String(s.ppa),
              alW: (s.al / mx('al') * 100).toFixed(1) + '%',
              ayW: (s.ay / mx('ay') * 100).toFixed(1) + '%',
              ppaW: (s.ppa / mx('ppa') * 100).toFixed(1) + '%',
              sc: s.sc == null ? '—' : s.sc.toFixed(1),
              scW: s.sc == null ? '0%' : s.sc.toFixed(1) + '%',
              scC: s.sc == null ? '#5a667a' : this.scoreC(s.sc, SD.flat),
              fw: ci === st.sel ? 600 : 400,
              bg: ci === st.sel ? 'rgba(46,179,238,0.12)' : 'transparent',
              edge: ci === st.sel ? "inset 0.277778vh 0 0 " + pal[0] : 'none',
              cBox: on ? col : '#3a475c',
              cFill: on ? col : 'transparent',
              cCheck: on ? '✓' : '',
              onClick: () => this.setState({
                sel: ci,
                cmp8: []
              }),
              enter: () => {
                clearTimeout(this._rkT);
                if (this.state.rkHover !== ci) this.setState({
                  rkHover: ci,
                  rkHd: null
                });
              },
              leave: () => {
                clearTimeout(this._rkT);
                this._rkT = setTimeout(() => this.setState({
                  rkHover: null,
                  rkHd: null
                }), 250);
              },
              onCmp: e => {
                e.stopPropagation();
                if (ci === st.sel) return;
                this.setState(x => {
                  const c = x.cmp8 || [];
                  return {
                    cmp8: c.includes(ci) ? c.filter(y => y !== ci) : [...c, ci]
                  };
                });
              }
            };
          })
        };
      })(),
      evoTabs: [['evo', 'Evolución'], ['ppa', 'Pellets perdidos acumulados']].map(([k, label]) => ({
        label,
        color: (st.evoTab || 'evo') === k ? '#e4e9f0' : '#5a667a',
        bd: (st.evoTab || 'evo') === k ? 'oklch(0.78 0.12 250)' : 'transparent',
        onClick: () => this.setState({
          evoTab: k
        })
      })),
      tabEvo: st.sMetric !== 'ppacum',
      tabPpa: st.sMetric === 'ppacum',
      tabInsight: st.sMetric === 'ppacum' ? (() => {
        const R = D.rows.map(r => r.cells[N - 1].cum),
          mx = R.indexOf(Math.max(...R));
        return 'Mayor pérdida acumulada: ' + D.rows[mx].name + ' (' + R[mx] + ') · ' + D.cages[st.sel] + ': ' + R[st.sel];
      })() : this.evoSimple(st, tol).sInsight,
      cols: cols.map((c, i) => ({
        ...c,
        short: ['SFR', 'Alarmas', 'Al./visita', 'PPA', 'Ayunos'][i]
      })),
      ...this.fitVals(),
      selName: D.cages[st.sel],
      dayName: D.days[st.day],
      selPrev: () => this.setState(s => ({
        sel: (s.sel + D.cages.length - 1) % D.cages.length
      })),
      selNext: () => this.setState(s => ({
        sel: (s.sel + 1) % D.cages.length
      })),
      kpis: (() => {
        const c = D.rows[st.sel].cells[st.day];
        if (c.ay) return [{
          l: 'Estado',
          v: 'Ayuno',
          c: '#e4e9f0'
        }];
        return [{
          l: 'Alarmas',
          v: String(c.al),
          c: c.al > 3 ? 'oklch(0.72 0.17 25)' : '#e4e9f0'
        }, {
          l: 'Visitas',
          v: String(c.vis),
          c: '#e4e9f0'
        }, {
          l: 'PPA',
          v: String(c.ppa),
          c: 'oklch(0.8 0.13 85)'
        }, {
          l: 'SFR',
          v: Number.isFinite(c.sfr) ? Math.round(c.sfr) + ' %' : '—',
          c: Math.abs(c.sfr - 100) > tol ? c.sfr < 100 ? 'oklch(0.7 0.14 250)' : 'oklch(0.72 0.15 55)' : '#e4e9f0'
        }];
      })(),
      tRows: tableRows.map((r, i) => {
        const ci = sorted[i].ci,
          cmp = st.cmp8 || [],
          on = ci === st.sel || cmp.includes(ci);
        const pal = ['oklch(0.78 0.12 250)', 'oklch(0.78 0.14 160)', 'oklch(0.72 0.16 320)', 'oklch(0.84 0.14 90)', 'oklch(0.72 0.17 25)', 'oklch(0.78 0.11 200)', 'oklch(0.74 0.15 55)', 'oklch(0.7 0.12 290)'];
        const order = [st.sel, ...cmp.filter(c => c !== st.sel)],
          col = pal[order.indexOf(ci) % pal.length];
        const on2 = st.rkHover === ci,
          cell = D.rows[ci].cells[st.day];
        let tip = {
          tipD: 'none',
          tDay: '',
          tRows: [],
          tSpark: [],
          tMax: ''
        };
        if (on2) {
          const live = D.rows.map(x => x.cells[st.day]).filter(c => !c.ay),
            av = k => live.length ? live.reduce((a, c) => a + c[k], 0) / live.length : 0;
          const al = D.rows[ci].cells.map(c => c.al),
            mx = Math.max(...al, 1);
          const R = [['SFR', 'sfr', v => v + ' %', v => v.toFixed(0) + ' %', 'oklch(0.72 0.15 55)'], ['Alarmas', 'al', v => String(v), v => v.toFixed(1), 'oklch(0.72 0.17 25)'], ['Al. / visita', 'apv', v => v.toFixed(2), v => v.toFixed(2), 'oklch(0.72 0.17 25)'], ['Al. / t', 'apt', v => v.toFixed(2), v => v.toFixed(2), 'oklch(0.72 0.17 25)'], ['PPA', 'ppa', v => String(v), v => v.toFixed(0), 'oklch(0.8 0.13 85)'], ['Visitas', 'vis', v => String(v), v => v.toFixed(1), 'oklch(0.76 0.09 250)']];
          tip = {
            tipD: 'flex',
            tDay: D.days[st.day],
            tMax: String(mx),
            tRows: cell.ay ? [{
              l: 'Estado',
              v: 'Ayuno',
              c: 'oklch(0.72 0.12 300)',
              avg: ''
            }] : R.map(([l, k, fv, fa, c]) => ({
              l,
              c,
              v: fv(cell[k]),
              avg: 'prom. ' + fa(av(k))
            })),
            tSpark: al.map((v, d) => ({
              h: (v / mx * 100).toFixed(1) + '%',
              c: d === st.day ? '#e4e9f0' : D.rows[ci].cells[d].ay ? '#2a3446' : 'oklch(0.62 0.16 25)'
            }))
          };
        }
        return {
          ...r,
          ...tip,
          enter: () => this.state.rkHover !== ci && this.setState({
            rkHover: ci
          }),
          leave: () => this.setState({
            rkHover: null
          }),
          fw: ci === st.sel ? 600 : 400,
          edge: ci === st.sel ? "inset 0.277778vh 0 0 " + pal[0] : 'none',
          onClick: () => this.setState({
            sel: ci,
            cmp8: []
          }),
          cBox: on ? col : '#3a475c',
          cFill: on ? col : 'transparent',
          cCheck: on ? '✓' : '',
          onCmp: e => {
            e.stopPropagation();
            if (ci === st.sel) return;
            this.setState(s => {
              const c = s.cmp8 || [];
              return {
                cmp8: c.includes(ci) ? c.filter(x => x !== ci) : [...c, ci]
              };
            });
          }
        };
      }),
      tableRows,
      tierSegs,
      tierTip,
      tierLeave: () => this.setState({
        tierHov: null
      }),
      tierTotal: tierSegs.reduce((a, t) => a + t.n, 0),
      tierList: D.tiers[st.tier],
      ppaLines,
      ppaMax: String(pMax),
      ppaMid: String(pMax / 2),
      ppaMove,
      ppaLeave: () => this.setState({
        ppaHoverDay: null
      }),
      ppaWheel,
      ppaKeyDown,
      ppaTipDisp: ppaHoverDay == null ? 'none' : 'flex',
      ppaTipX: ppaHoverDay == null ? '0%' : (ppaHoverDay / Math.max(1, N - 1) * 100).toFixed(2) + '%',
      ppaTipTx: ppaHoverDay != null && ppaHoverDay / Math.max(1, N - 1) > .62 ? "translateX(calc(-100% - 0.625vw))" : "translateX(0.625vw)",
      ppaTipDate: ppaHoverDay == null ? '' : D.days[ppaHoverDay],
      ppaTipRows,
      ppaXTicks: ppaTickIndices.map(di => ({
        key: di,
        label: shortDay(D.days[di])
      })),
      selDayLine: `M${(st.day / Math.max(1, N - 1) * 600).toFixed(1)} 0 V100`,
      det,
      ...this.depthProfile(det, st.exp === 'det' ? 240 : 46),
      ...this.alarmVals(det),
      ...this.noteVals(det),
      ...this.detHover(det, this.props.currentThreshold ?? 20),
      detTitle: `${D.cages[st.sel]} · ${D.days[st.day]}`,
      detSummary: cell.ay ? 'Día de ayuno · sin alimentación' : det.summary,
      prevDay: () => this.setState(s => ({
        day: Math.max(0, s.day - 1)
      })),
      nextDay: () => this.setState(s => ({
        day: Math.min(N - 1, s.day + 1)
      }))
    };
  }
};
export default logic;
